import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/stores/auth.store'
import { authApi, handleAuthToken } from '@/features/auth/api/auth.api'
import { getAccessToken, setAccessToken, scheduleTokenRefresh, silentRefresh } from '@/shared/api/client'
import { clearQueuePersistedState } from '@/features/workspace/hooks/use-queue-page'
import { useQueueContextStore } from '@/features/workspace/stores/queue-context.store'
import { useHistoryContextStore } from '@/features/workspace/stores/history-context.store'
import { useClientAdminFiltersStore } from '@/features/client-admin/stores/dashboard-filters.store'
import { useEngineAdminFiltersStore } from '@/features/engine-admin/stores/dashboard-filters.store'
import { usePipelineHandoffStore } from '@/shared/stores/pipeline-handoff.store'
import { flushAnalyticsOnLogout } from '@/shared/analytics'

/**
 * Initialize auth state on mount.
 * Flow: try silent refresh first (to recover token from httpOnly cookie),
 * then call /auth/me with the fresh token. This avoids a spurious 401.
 */
export function useAuthInit() {
  const { setUser, setLoading, setBootstrapped, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      setBootstrapped(true)
      return
    }

    // Skip auth init on auth pages to avoid refresh loops
    if (window.location.pathname.startsWith('/auth/')) {
      setLoading(false)
      setBootstrapped(true)
      return
    }

    let cancelled = false

    async function loadUser() {
      setLoading(true)
      try {
        // If no in-memory token, attempt a silent refresh first using httpOnly cookie.
        // This avoids the 401 → refresh → retry cycle on every page load.
        let expiresIn = 900
        if (!getAccessToken()) {
          const refreshed = await silentRefresh()
          if (cancelled) return
          setAccessToken(refreshed.access_token)
          expiresIn = refreshed.expires_in
        }

        const res = await authApi.me()
        if (!cancelled) {
          setUser(res.data)
          scheduleTokenRefresh(expiresIn)
        }
      } catch {
        // Not authenticated — clear any stale token and let the app redirect to login
        if (!cancelled) {
          setAccessToken(null)
          setLoading(false)
        }
      } finally {
        if (!cancelled) setBootstrapped(true)
      }
    }

    loadUser()
    return () => { cancelled = true }
  }, [isAuthenticated, setUser, setLoading, setBootstrapped])
}

/** Returns a logout function that clears all auth state and calls POST /auth/logout */
export function useLogout() {
  const { clearUser } = useAuthStore()
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Flush any queued analytics before dropping the JWT — otherwise the next
    // POST goes out unauthenticated.
    flushAnalyticsOnLogout()
    authApi.logout() // fire-and-forget (best-effort server-side cleanup)
    setAccessToken(null)
    clearUser()
    queryClient.clear()
    // Reset queue tab/filters so the next sign-in lands on Copilot, not the
    // previous session's last-selected tab.
    clearQueuePersistedState()
    useQueueContextStore.getState().clear()
    // /workspace/history's snapshot is sessionStorage-backed too, so it
    // outlives the redirect below exactly like the queue's does — without
    // this the next user on a shared workstation opens History on the
    // previous user's client/facility/date filters.
    useHistoryContextStore.getState().clear()
    // Reset dashboard filter stores — sessionStorage survives a re-login on
    // the same tab, so without this the next user inherits the previous
    // user's client/facility picks (visible bug: lands on Charts Dashboard
    // with someone else's Prima CARE PC + Endoscopy preselected).
    useClientAdminFiltersStore.getState().reset()
    useEngineAdminFiltersStore.getState().reset()
    usePipelineHandoffStore.getState().clear()
    // Hard-redirect to /auth/login (no ?next=) so AppShell never gets a
    // render with !isAuthenticated on the protected route — otherwise it
    // would bounce to /auth/login?next=<current-path>, which would send the
    // user back to whatever page they signed out from on the next login.
    // Deep-link arrivals (unauthenticated user opens a protected URL fresh)
    // still go through AppShell's ?next= path, so that flow keeps working.
    window.location.replace('/auth/login')
  }, [clearUser, queryClient])
}

/**
 * Imperative logout for use outside React components (e.g. WebSocket handlers).
 * Redirects via window.location since React Router is unavailable.
 */
export function forceLogout() {
  flushAnalyticsOnLogout()
  authApi.logout()
  setAccessToken(null)
  useAuthStore.getState().clearUser()
  clearQueuePersistedState()
  useQueueContextStore.getState().clear()
  useHistoryContextStore.getState().clear()
  useClientAdminFiltersStore.getState().reset()
  useEngineAdminFiltersStore.getState().reset()
  usePipelineHandoffStore.getState().clear()
  window.location.href = '/auth/login'
}

/** Convenience hook — re-exports store selectors */
export function useAuth() {
  return useAuthStore()
}

/** Re-export for convenience */
export { handleAuthToken } from '@/features/auth/api/auth.api'
